import { z } from 'zod';

// Mesmas opções/default das listagens de Programas e Empresas (DEV-315, 09/10/2026 —
// antes Vagas era a única com 16).
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const SORT_KEYS = ['name', 'status', 'createdAt', 'updatedAt'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const JOB_STATUS_TOKENS = [
  'rascunho',
  'ativa-privada',
  'ativa',
  'cancelada',
  'encerrada',
] as const;
export type JobStatusToken = (typeof JOB_STATUS_TOKENS)[number];

/** Coerce single-vs-array inputs in URL search-params into a deduped array. */
function arrayCatch<T>(s: z.ZodType<T>) {
  return z
    .union([s, z.array(s)])
    .optional()
    .transform((v) =>
      v === undefined ? undefined : Array.from(new Set(Array.isArray(v) ? v : [v]))
    )
    .catch(undefined);
}

/**
 * Zod schema for the `/vagas` route's URL search params. Drives TanStack
 * Router's `validateSearch` so:
 *   - state is bookmarkable, share-linkable, reload-safe;
 *   - malformed URLs fall back to defaults (`.catch()` per field);
 *   - the search shape is the single source of truth for query construction.
 */
export const JobsSearchSchema = z
  .object({
    // Workspace scope (App Consultor) — the company entered from the Home.
    // The route guard requires it; a malformed value falls back to undefined
    // (→ the guard bounces to the Home).
    companyId: z.string().uuid().optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    // DEV-315 — UM programa por vez: a listagem só busca vagas com um programa
    // escolhido. Link antigo com lista (`programId=[...]`) cai no `.catch` →
    // sem programa → placeholder "Selecione um programa".
    programId: z.string().uuid().optional().catch(undefined),
    status: arrayCatch(z.enum(JOB_STATUS_TOKENS)),
    sort: z.enum(SORT_KEYS).optional().catch(undefined),
    dir: z.enum(SORT_DIRECTIONS).catch('asc').default('asc'),
    page: z.coerce.number().int().positive().catch(1).default(1),
    pageSize: z
      .union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)])
      .catch(10)
      .default(10),
  })
  .strip();

export type JobsSearch = z.infer<typeof JobsSearchSchema>;

/**
 * Defaults equivalent to landing on `/vagas` with no query string. Callers
 * navigating from elsewhere can pass this as the `search` param to satisfy
 * TanStack's required-search-shape typing.
 */
export const DEFAULT_JOBS_SEARCH: JobsSearch = {
  companyId: undefined,
  search: undefined,
  programId: undefined,
  status: undefined,
  sort: undefined,
  dir: 'asc',
  page: 1,
  pageSize: 10,
};
